import React from "react";
import { CheckCircle2 } from "lucide-react";

const PlanSection = ({ title, items }) => (
    <div className="mt-5 border-t border-slate-100 pt-5">
        <p className="text-sm font-bold text-slate-900">{title}</p>
        <div className="mt-3 space-y-2.5">
            {items.map((item) => (
                <div
                    key={`${title}-${item.label}`}
                    className="flex items-start justify-between gap-4 rounded-2xl bg-slate-50 px-4 py-3"
                >
                    <span className="min-w-0 text-sm font-medium leading-5 text-slate-600">
                        {item.label}
                    </span>
                    <span className="max-w-[48%] shrink-0 text-right text-sm font-bold leading-5 text-slate-950">
                        {item.value}
                    </span>
                </div>
            ))}
        </div>
    </div>
);

const BenefitsSection = ({ benefits }) => {
    if (!benefits?.length) return null;

    return (
        <div className="mt-5 border-t border-slate-100 pt-5">
            <p className="text-sm font-bold text-slate-900">Benefits</p>
            <div className="mt-3 space-y-2.5">
                {benefits.map((benefit) => (
                    <div
                        key={benefit}
                        className="flex items-start gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-medium leading-5 text-slate-700"
                    >
                        <CheckCircle2
                            size={16}
                            className="mt-0.5 shrink-0 text-indigo-600"
                            aria-hidden="true"
                        />
                        <span>{benefit}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const SubscriptionPlanCard = ({
    plan,
    isCurrentPlan,
    isUpgradeOption = false,
}) => (
    <article
        className={`relative flex h-full min-h-full flex-col rounded-3xl border p-6 shadow-sm transition ${
            isCurrentPlan
                ? "border-indigo-400 bg-white shadow-[0_15px_45px_rgba(79,70,229,0.14)] ring-2 ring-indigo-100"
                : isUpgradeOption
                    ? "border-violet-300 bg-violet-100 hover:border-violet-400 hover:shadow-md"
                : "border-slate-200 hover:border-indigo-100 hover:shadow-md"
        }`}
    >
        {isCurrentPlan && (
            <div className="absolute inset-x-6 top-0 h-1 rounded-b-full bg-indigo-600" />
        )}
        <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-indigo-600">
                    Plan
                </p>
                <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-950">
                    {plan.name}
                </h2>
            </div>
            {isCurrentPlan && (
                <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-100">
                    Current Plan
                </span>
            )}
        </div>

        <div className="mt-4 rounded-2xl bg-indigo-50/60 px-4 py-3">
            <p className="text-xs font-bold uppercase tracking-wide text-indigo-700">
                Best for
            </p>
            <p className="mt-1 text-sm leading-6 text-slate-700">
                {plan.bestFor}
            </p>
        </div>

        <div className="flex flex-1 flex-col">
            <PlanSection title="Monthly credits" items={plan.monthlyCredits} />
            <PlanSection
                title="Operational limits"
                items={plan.operationalLimits}
            />
            <BenefitsSection benefits={plan.benefits} />
        </div>
    </article>
);

export default SubscriptionPlanCard;
